import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useInventory, useInventoryCategories, usePageTitle, useView } from '@hooks';
import { L } from '@lib';
import { Inventory } from '@services';
import { useStore } from '@stores';
import { Button, Space } from 'antd';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef } from 'react';
import { InventoryImportDrawer, InventoryImportDrawerHandle } from './components/InventoryImportDrawer';
import { InventoryItemDrawer, InventoryItemDrawerHandle } from './components/InventoryItemDrawer';
import {
  InventoryUploadDrawer,
  InventoryUploadDrawerHandle,
} from './components/InventoryUploadDrawer';
import Presentation from './InventoryTable.presentation';

interface IProps {
  embedded?: boolean;
}

export const InventoryTable: React.FC<IProps> = observer(({ embedded }) => {
  const { isMobile } = useView();
  const inventoryHook = useInventory();
  const { options: categoryOptions } = useInventoryCategories();
  const { inventoryStore } = useStore();
  const { setHeaderActions } = usePageTitle();

  const itemDrawerRef = useRef<InventoryItemDrawerHandle>(null);
  const uploadDrawerRef = useRef<InventoryUploadDrawerHandle>(null);
  const importDrawerRef = useRef<InventoryImportDrawerHandle>(null);

  const openUpload = useCallback(
    (prefill?: { items?: any[]; supplierId?: string }) => uploadDrawerRef.current?.open(prefill),
    []
  );

  useEffect(() => {
    if (embedded) return;
    setHeaderActions(headerButtons);
    return () => setHeaderActions(null);
  }, [isMobile, openUpload, embedded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdit = (record: Inventory) => itemDrawerRef.current?.edit(record);

  const handleDuplicate = (record: Inventory) =>
    openUpload({
      items: [
        {
          name: record.name,
          reference: record.reference,
          partNumber: record.partNumber,
          description: record.description,
          quantity: 1,
          costPrice: record.costPrice,
          sellPrice: record.sellPrice,
          categoryId: record.categoryId,
          isInShop: record.isInShop,
          location: record.location,
        },
      ],
      supplierId: record.supplierId,
    });

  const handleDelete = async (id: string) => {
    await inventoryStore.delete({ id });
    inventoryHook.refresh();
  };

  const headerButtons = (
    <Space>
      <Button
        icon={<UploadOutlined />}
        onClick={() => importDrawerRef.current?.open()}
        style={{ backgroundColor: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }}
      >
        {!isMobile ? L('Import Excel') : null}
      </Button>
      <Button
        icon={<PlusOutlined />}
        onClick={() => openUpload()}
        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
      >
        {!isMobile ? L('New Inventory') : null}
      </Button>
    </Space>
  );

  return (
    <>
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          {headerButtons}
        </div>
      )}
      <Presentation
        inventoryHook={inventoryHook}
        categoryOptions={categoryOptions}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
      />
      <InventoryItemDrawer
        ref={itemDrawerRef}
        categoryOptions={categoryOptions}
        onSuccess={inventoryHook.refresh}
        onDelete={handleDelete}
      />
      <InventoryUploadDrawer
        ref={uploadDrawerRef}
        categoryOptions={categoryOptions}
        onSuccess={() => inventoryHook.refresh()}
      />
      <InventoryImportDrawer
        ref={importDrawerRef}
        onSuccess={() => inventoryHook.refresh()}
      />
    </>
  );
});
